import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from "../services/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "../components/ui/dialog";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { 
  Plus, 
  Search, 
  Building2, 
  User, 
  Edit, 
  Trash2,
  Mail,
  Phone,
  Users,
  FilePlus,
  FileText,
  FileMinus
} from "lucide-react";
import { toast } from "sonner";

const CustomersPage = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [form, setForm] = useState({
    customer_type: "professional",
    company_name: "",
    first_name: "",
    last_name: "",
    address_complement: "",
    address: "",
    city: "",
    postal_code: "",
    country: "France",
    email: "",
    phone: "",
    siret: "",
    vat_number: "",
    notes: ""
  });

  useEffect(() => {
    loadCustomers();
  }, [search, filterType]);

  useEffect(() => {
    const editId = new URLSearchParams(window.location.search).get("edit");
    if (editId && customers.length > 0) {
      const c = customers.find(x => x.id === editId);
      if (c) {
        openDialog(c);
        window.history.replaceState({}, "", "/clients");
      }
    }
  }, [customers]);

  const loadCustomers = async () => {
    try {
      const res = await getCustomers({ 
        search: search || undefined,
        customer_type: filterType && filterType !== "all" ? filterType : undefined 
      });
      setCustomers(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      setForm(customer);
    } else {
      setEditingCustomer(null);
      setForm({
        customer_type: "professional",
        company_name: "",
        first_name: "",
        last_name: "",
        address_complement: "",
        address: "",
        city: "",
        postal_code: "",
        country: "France",
        email: "",
        phone: "",
        siret: "",
        vat_number: "",
        notes: ""
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, form);
        toast.success("Client mis à jour");
      } else {
        await createCustomer(form);
        toast.success("Client créé");
      }
      setDialogOpen(false);
      loadCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Archiver ce client ?")) return;
    try {
      await deleteCustomer(id);
      toast.success("Client archivé");
      loadCustomers();
    } catch (err) {
      toast.error("Erreur lors de l'archivage");
    }
  };

  const getCustomerName = (customer) => {
    if (customer.company_name) return customer.company_name;
    const fullName = `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
    return fullName || "Sans nom";
  };

  return (
    <div className="p-4 lg:p-6 xl:p-8 space-y-4 lg:space-y-6 animate-fadeIn" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-3">
            <Users size={28} className="text-[#26788A]" />
            Clients
          </h1>
          <p className="text-slate-500 mt-1 text-sm lg:text-base">{customers.length} clients actifs</p>
        </div>
        <Button
          data-testid="new-customer-btn"
          className="btn-primary w-full sm:w-auto"
          onClick={() => openDialog()}
        >
          <Plus size={16} className="mr-2" />
          Nouveau client
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            data-testid="search-customers-input"
            placeholder="Rechercher un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 input-field"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48 input-field">
            <SelectValue placeholder="Type de client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="professional">Professionnels</SelectItem>
            <SelectItem value="individual">Particuliers</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Customer List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement...</div>
      ) : customers.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500">Aucun client trouvé</p>
            <Button
              className="mt-4 btn-primary"
              onClick={() => openDialog()}
            >
              <Plus size={16} className="mr-2" />
              Créer votre premier client
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.map((customer) => (
            <Card 
              key={customer.id} 
              className="bg-white border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
              data-testid={`customer-card-${customer.id}`}
              onClick={() => navigate(`/reports/client/${customer.id}`)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      {customer.customer_type === "professional" ? (
                        <Building2 size={18} className="text-[#26788A]" />
                      ) : (
                        <User size={18} className="text-[#26788A]" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {getCustomerName(customer)}
                      </p>
                      <p className="text-xs text-slate-500 font-mono uppercase">
                        {customer.customer_type === "professional" ? "Pro" : "Particulier"}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-[#26788A]"
                          data-testid={`create-doc-btn-${customer.id}`}
                          onClick={(e) => e.stopPropagation()}
                          title="Créer un document"
                        >
                          <FilePlus size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem
                          data-testid={`create-invoice-${customer.id}`}
                          onClick={() => navigate(`/document/new/invoice?customer=${customer.id}`)}
                        >
                          <FileText size={14} className="mr-2 text-[#26788A]" />
                          Nouvelle facture
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={`create-quote-${customer.id}`}
                          onClick={() => navigate(`/document/new/quote?customer=${customer.id}`)}
                        >
                          <FileText size={14} className="mr-2 text-blue-600" />
                          Nouveau devis
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={`create-credit-${customer.id}`}
                          onClick={() => navigate(`/document/new/credit?customer=${customer.id}`)}
                        >
                          <FileMinus size={14} className="mr-2 text-red-500" />
                          Nouvel avoir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-[#26788A]"
                      data-testid={`edit-customer-${customer.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDialog(customer);
                      }}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-slate-400 hover:text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(customer.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                
                <div className="mt-4 space-y-1">
                  {customer.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail size={12} />
                      <span className="truncate">{customer.email}</span>
                    </div>
                  )}
                  {customer.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone size={12} />
                      <span>{customer.phone}</span>
                    </div>
                  )}
                  {customer.city && (
                    <p className="text-xs text-slate-500">
                      {customer.postal_code} {customer.city}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-slate-900">
              {editingCustomer ? "Modifier le client" : "Nouveau client"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Type de client
              </Label>
              <Select 
                value={form.customer_type} 
                onValueChange={(v) => setForm({ ...form, customer_type: v })}
              >
                <SelectTrigger className="mt-2 input-field">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professionnel (B2B)</SelectItem>
                  <SelectItem value="individual">Particulier (B2C)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.customer_type === "professional" ? (
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Raison sociale *
                </Label>
                <Input
                  data-testid="customer-company-name-input"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  className="mt-2 input-field"
                  placeholder="Nom de l'entreprise"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Prénom
                  </Label>
                  <Input
                    data-testid="customer-first-name-input"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="mt-2 input-field"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    Nom
                  </Label>
                  <Input
                    data-testid="customer-last-name-input"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="mt-2 input-field"
                  />
                </div>
              </div>
            )}

            {/* Complément d'adresse / À l'attention de */}
            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Complément <span className="text-slate-400 font-normal">(optionnel)</span>
              </Label>
              <Input
                data-testid="customer-address-complement-input"
                value={form.address_complement || ""}
                onChange={(e) => setForm({ ...form, address_complement: e.target.value })}
                className="mt-2 input-field"
                placeholder="À l'attention de..., Titre, Service..."
              />
            </div>

            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Adresse
              </Label>
              <Input
                data-testid="customer-address-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-2 input-field"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Code postal
                </Label>
                <Input
                  data-testid="customer-postal-code-input"
                  value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                  className="mt-2 input-field"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Ville
                </Label>
                <Input
                  data-testid="customer-city-input"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="mt-2 input-field"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Email
                </Label>
                <Input
                  data-testid="customer-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 input-field"
                />
              </div>
              <div>
                <Label className="text-slate-600 text-xs uppercase tracking-wider">
                  Téléphone
                </Label>
                <Input
                  data-testid="customer-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 input-field"
                />
              </div>
            </div>

            {form.customer_type === "professional" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    SIRET
                  </Label>
                  <Input
                    data-testid="customer-siret-input"
                    value={form.siret}
                    onChange={(e) => setForm({ ...form, siret: e.target.value })}
                    className="mt-2 input-field"
                    placeholder="123 456 789 00012"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 text-xs uppercase tracking-wider">
                    TVA Intracom.
                  </Label>
                  <Input
                    data-testid="customer-vat-input"
                    value={form.vat_number}
                    onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                    className="mt-2 input-field"
                    placeholder="FR12345678901"
                  />
                </div>
              </div>
            )}

            <div>
              <Label className="text-slate-600 text-xs uppercase tracking-wider">
                Notes
              </Label>
              <Textarea
                data-testid="customer-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 input-field min-h-20"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-slate-200 text-slate-600"
            >
              Annuler
            </Button>
            <Button
              data-testid="save-customer-btn"
              onClick={handleSubmit}
              className="btn-primary"
            >
              {editingCustomer ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
